import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { SITE_URL, canonicalUrl } from '../seo/siteRoutes.js';

const PAGE_PATH = '/garments-quality';
const PAGE_URL = canonicalUrl(PAGE_PATH);
const META_DESCRIPTION =
  'איך בוחרים ביגוד להדפסה: הרכב בד, גזרה, מידות והוראות טיפול, עם הפניה לפרטי כל מוצר בקטלוג Printeam.';

export default function GarmentsQuality() {
  return (
    <div className="min-h-screen py-12">
      <Helmet>
        {/* Structured data: Organization, Article, BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Printeam",
            url: canonicalUrl('/'),
            logo: `${SITE_URL}/logo_printee-optimized.webp`
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "איכות הביגוד – Printeam",
            description: META_DESCRIPTION,
            author: { "@type": "Organization", name: "Printeam" },
            publisher: {
              "@type": "Organization",
              name: "Printeam",
              logo: { "@type": "ImageObject", url: `${SITE_URL}/logo_printee-optimized.webp` }
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": PAGE_URL }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "ראשי", item: canonicalUrl('/') },
              { "@type": "ListItem", position: 2, name: "איכות הביגוד", item: PAGE_URL }
            ]
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-6">איכות הביגוד</h1>

        <section>
          <h2 className="text-2xl font-semibold mt-4 mb-2">הבד והגזרה הם חלק מהבחירה</h2>
          <p className="text-gray-700">
            לפני שמחליטים על עיצוב, כדאי לבדוק מאיזה בד עשוי הפריט, אילו מידות
            וצבעים מוצעים בו ואילו אזורי מיתוג זמינים. הנתונים משתנים בין SKU אחד
            לאחר, ולכן פרטי המוצר בקטלוג הם המקור לבחירה.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">סוגי בד נפוצים בקטלוג</h2>
          <p className="text-gray-700">
            הקטלוג כולל פריטים בהרכבי בד שונים. ההרכב המדויק מופיע בדף המוצר כאשר
            הוא זמין, ואין להסיק מהרכב של דגם אחד על דגם אחר.
          </p>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>פריטי כותנה, ובהם דגמים שמוצגים כ־100% כותנה.</li>
            <li>תערובות כותנה ופוליאסטר בפריטים שבהם נדרש שילוב בין שני החומרים.</li>
            <li>פריטי פוליאסטר ו־DryFit לשימושים שבהם הרכב כזה מתאים.</li>
          </ul>
          <p className="text-gray-700 mt-3">
            אם מאפיין מסוים חשוב להזמנה, יש לאמת אותו בדף המוצר ובחירה מדויקת של
            צבע ומידה לפני התשלום.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">גזרות ומידות</h2>
          <p className="text-gray-700">
            טווח המידות והגזרה אינם אחידים לכל המוצרים. יש דגמי יוניסקס, דגמים
            לילדים ופריטים עם טווחי מידות אחרים.
          </p>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>בדקו את המידות הזמינות בדף המוצר שנבחר.</li>
            <li>בחרו כמות נפרדת לכל שילוב של צבע ומידה.</li>
            <li>אל תניחו שמידה זהה יושבת באותה צורה בכל דגם.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">טיפול בפריט המודפס</h2>
          <p className="text-gray-700">
            הוראות הטיפול תלויות בבד ובשיטת המיתוג. כאשר הוראות מוצר זמינות,
            הן מופיעות בדף שלו ויש לפעול לפיהן.
          </p>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>הפרידו כביסה לפי צבע ובהתאם לתווית היצרן.</li>
            <li>הימנעו מחום או טיפול שאינם תואמים להוראות הפריט.</li>
            <li>במקרה של ספק, פנו לפני הכביסה הראשונה.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">התאמת הפריט למיתוג</h2>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>בחרו מוצר לפי ההרכב, הגזרה והשימוש המתוכנן.</li>
            <li>בדקו את אזורי המיתוג הפעילים בדף המוצר.</li>
            <li>אשרו בנפרד כל צבע, מידה וכמות שנוספו להזמנה.</li>
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="text-2xl font-semibold mb-2">בחירת מוצר ומדיניות</h2>
          <p className="text-gray-700">
            לצפייה בהרכבים, בצבעים ובמידות עברו אל{' '}
            <Link to="/catalog" className="text-blue-600 hover:underline">קטלוג המוצרים</Link>.
            מידע על מוצרים מותאמים אישית מופיע גם ב
            <Link to="/returns" className="text-blue-600 hover:underline">מדיניות ההחזרות</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}
